import has from 'lodash-es/has'
import isNil from 'lodash-es/isNil'
import omit from 'lodash-es/omit'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../constants/common'

import {
  APPOINTMENT_MOCK_REQUEST_COPIED_FIELDS,
  AppointmentSendKind,
  AppointmentSendState,
} from './Appointment.constants'
import { getAppointmentMockId } from './Appointment.mock.utils'
import { getIsAppointmentExternalParticipant } from './Appointment.utils'

function toAppointmentMockAnchorValue({ anchor, id }) {
  return isNil(id) ? null : { ...anchor, id }
}

function toAppointmentMockParticipant({
  createdAt,
  organizerId,
  participant,
  profile,
}) {
  const email = participant.email || profile.email || null

  const isInvited =
    getIsAppointmentExternalParticipant(participant) && !isNil(email)

  return {
    can_resend: false,
    cancelled_notified_at: null,
    contact: participant.contact ?? null,
    display_name: participant.display_name ?? profile.display_name ?? null,
    email,
    id: getAppointmentMockId(),
    is_organizer:
      !isNil(organizerId) && participant.user_profile === organizerId,
    kind: participant.kind,
    last_send_at: isInvited ? createdAt : null,
    last_send_kind: isInvited ? AppointmentSendKind.invitation : null,
    last_send_state: isInvited ? AppointmentSendState.sent : null,
    notified_at: isInvited ? createdAt : null,
    reminder_sent_at: null,
    user_profile: participant.user_profile ?? null,
  }
}

export function toAppointmentMockAnchor({ data, mockContext }) {
  const anchors = mockContext.anchors ?? EMPTY_OBJECT

  const request = toAppointmentMockAnchorValue({
    anchor: anchors.request,
    id: data.request,
  })

  return {
    building: request
      ? (request.building ?? null)
      : toAppointmentMockAnchorValue({
          anchor: anchors.building,
          id: data.building,
        }),
    flat: request
      ? (request.flat ?? null)
      : toAppointmentMockAnchorValue({ anchor: anchors.flat, id: data.flat }),
    request: request && omit(request, APPOINTMENT_MOCK_REQUEST_COPIED_FIELDS),
    resident: toAppointmentMockAnchorValue({
      anchor: anchors.resident,
      id: data.resident,
    }),
  }
}

export function getAppointmentMockUpdatedAnchor({ data, mockContext, record }) {
  const anchors = mockContext.anchors ?? EMPTY_OBJECT

  const isPlaceEditable = !record.anchor.request

  return isNil(data.request)
    ? {
        ...record.anchor,
        building:
          isPlaceEditable && has(data, 'building')
            ? toAppointmentMockAnchorValue({
                anchor: anchors.building,
                id: data.building,
              })
            : record.anchor.building,
        flat:
          isPlaceEditable && has(data, 'flat')
            ? toAppointmentMockAnchorValue({
                anchor: anchors.flat,
                id: data.flat,
              })
            : record.anchor.flat,
      }
    : {
        ...toAppointmentMockAnchor({ data, mockContext }),
        resident: record.anchor.resident,
      }
}

export function toAppointmentMockEmailThread({ data, mockContext }) {
  return toAppointmentMockAnchorValue({
    anchor: mockContext.anchors?.emailThread,
    id: data.email_thread,
  })
}

export function toAppointmentMockParticipants({
  createdAt,
  data,
  mockContext,
  organizerId,
}) {
  const profiles = mockContext.participants ?? EMPTY_LIST

  return (data.participants ?? EMPTY_LIST).map((participant, index) =>
    toAppointmentMockParticipant({
      createdAt,
      organizerId,
      participant,
      profile: profiles[index] ?? EMPTY_OBJECT,
    })
  )
}
